import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Divider, FormHelperText, MenuItem, Stack, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import { type FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { type MediaProviderName, useUpdateMediaProvider } from '../../api/index.js';
import useSnackBar from '../../hooks/use-snack-bar.js';

// '' is the form representation of "no override" and maps back to null on submit.
const DEFAULT_OPTION = '';

const PROVIDER_OPTIONS = [
  { value: DEFAULT_OPTION, label: 'Environment default' },
  { value: 'antmedia', label: 'Ant Media' },
  { value: 'mediamtx', label: 'MediaMTX' },
];

const mediaProviderSchema = yup.object({
  mediaProvider: yup.string().oneOf(PROVIDER_OPTIONS.map((o) => o.value)).defined(),
});

type MediaProviderFormValues = yup.InferType<typeof mediaProviderSchema>;

interface PatientMediaProviderFormProps {
  patientId: string;
  mediaProvider: MediaProviderName | undefined;
  canUpdate: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

const PatientMediaProviderForm: FC<PatientMediaProviderFormProps> = ({
  patientId,
  mediaProvider,
  canUpdate,
  onDirtyChange,
}) => {
  const snackBar = useSnackBar();
  const mutation = useUpdateMediaProvider(patientId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<MediaProviderFormValues>({
    resolver: yupResolver(mediaProviderSchema),
    defaultValues: { mediaProvider: mediaProvider ?? DEFAULT_OPTION },
  });

  // Keep in sync when the patient row refetches after a save.
  useEffect(() => {
    reset({ mediaProvider: mediaProvider ?? DEFAULT_OPTION });
  }, [mediaProvider, reset]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const onSubmit = handleSubmit((values) => {
    const next = (values.mediaProvider === DEFAULT_OPTION ? null : values.mediaProvider) as MediaProviderName;
    mutation.mutate(next, {
      onSuccess: () => snackBar.success('Streaming backend updated.'),
      onError: () => snackBar.error('Failed to update the streaming backend.'),
    });
  });

  return (
    <Box>
      <Typography variant="h5" component="h3">Streaming backend</Typography>
      <FormHelperText sx={{ mb: 2 }}>
        Which media server serves this patient&apos;s live video. Changing it stops the
        patient&apos;s streams on the current server and restarts them on the new one — live
        tiles reconnect on their own after roughly a minute.
      </FormHelperText>

      <form onSubmit={onSubmit}>
        <Stack spacing={2}>
          <Controller
            name={'mediaProvider'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                value={field.value ?? DEFAULT_OPTION}
                label={'Media server'}
                disabled={!canUpdate}
                helperText={'Leave on the environment default unless this patient is part of a streaming trial.'}
              >
                {PROVIDER_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'default'} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Divider />
          <Stack direction={'row'} justifyContent={'flex-end'}>
            <LoadingButton
              type={'submit'}
              aria-label={'Save streaming backend'}
              loading={isSubmitting || mutation.isLoading}
              variant={'contained'}
              disabled={!_.isEmpty(errors) || !isDirty || !canUpdate}
            >
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default PatientMediaProviderForm;
